import type React from 'react';
import type { Annotation } from './types';
import { deleteAnnotationsByIdV2, saveAnnotationSnapshotV2 } from '../../lib/annotationV2Persistence';

type ActiveMode = 'draw' | 'specs' | 'rfi' | 'neca' | 'estimate';

export type CommitContext = {
  supabase: any;
  estimationId: string;
  activeMode: ActiveMode;
  currentDrawingFileId?: string | null;

  annotationsRef: React.MutableRefObject<Annotation[]>;
  setAnnotations: React.Dispatch<React.SetStateAction<Annotation[]>>;
  onAnnotationSaved?: (changedComponentTypes?: string[]) => Promise<void> | void;

  acquireSaveLock: () => Promise<void>;
  releaseSaveLock: () => void;
  setIsSaving?: (v: boolean) => void;

  canvasRef?: React.RefObject<HTMLCanvasElement | null>;
  // Optional hook invoked immediately before local state mutation.
  beforeLocalCommit?: () => void;
  persistFilter?: (annotation: Annotation) => boolean;
};

type CommitCreateResult = {
  next: Annotation[];
  changedComponentTypes: string[];
};

type CommitUpdateResult = {
  next: Annotation[];
  changedComponentTypes: string[];
  updatedIds: string[];
};

type PersistResult = {
  changedComponentTypes: string[];
};

type CommitClearScopeResult = PersistResult & {
  deletedCount: number;
};

const uniqueById = (rows: Annotation[]) => {
  const byId = new Map<string, Annotation>();
  for (const row of rows || []) {
    const id = String(row?.id || '').trim();
    if (!id) continue;
    byId.set(id, row);
  }
  return Array.from(byId.values());
};

const finiteOrUndefined = (value: any): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) ? value : undefined;

const canUseCanvasDims = (w?: number, h?: number) =>
  typeof w === 'number' && Number.isFinite(w) && w > 0 &&
  typeof h === 'number' && Number.isFinite(h) && h > 0;

const normalizePoint = (point: any, canvasW: number, canvasH: number) => {
  const x = finiteOrUndefined(point?.x);
  const y = finiteOrUndefined(point?.y);
  const relativeX = finiteOrUndefined(point?.relativeX) ?? (x !== undefined ? x / canvasW : undefined);
  const relativeY = finiteOrUndefined(point?.relativeY) ?? (y !== undefined ? y / canvasH : undefined);
  return {
    ...point,
    ...(x !== undefined ? { x } : {}),
    ...(y !== undefined ? { y } : {}),
    ...(relativeX !== undefined ? { relativeX } : {}),
    ...(relativeY !== undefined ? { relativeY } : {}),
  };
};

const normalizeRelativeGeometry = (
  annotation: Annotation,
  canvasW?: number,
  canvasH?: number
): Annotation => {
  if (!canUseCanvasDims(canvasW, canvasH)) return annotation;
  const width = canvasW as number;
  const height = canvasH as number;

  const x = finiteOrUndefined(annotation.x);
  const y = finiteOrUndefined(annotation.y);
  const startX = finiteOrUndefined(annotation.startX);
  const startY = finiteOrUndefined(annotation.startY);
  const annWidth = finiteOrUndefined(annotation.width);
  const annHeight = finiteOrUndefined(annotation.height);

  const relativeX = finiteOrUndefined(annotation.relativeX) ?? (x !== undefined ? x / width : undefined);
  const relativeY = finiteOrUndefined(annotation.relativeY) ?? (y !== undefined ? y / height : undefined);
  const relativeStartX =
    finiteOrUndefined(annotation.relativeStartX) ?? (startX !== undefined ? startX / width : undefined);
  const relativeStartY =
    finiteOrUndefined(annotation.relativeStartY) ?? (startY !== undefined ? startY / height : undefined);
  const relativeWidth =
    finiteOrUndefined(annotation.relativeWidth) ?? (annWidth !== undefined ? annWidth / width : undefined);
  const relativeHeight =
    finiteOrUndefined(annotation.relativeHeight) ?? (annHeight !== undefined ? annHeight / height : undefined);

  const points = Array.isArray((annotation as any).points)
    ? (annotation as any).points.map((point: any) => normalizePoint(point, width, height))
    : (annotation as any).points;

  return {
    ...annotation,
    ...(relativeX !== undefined ? { relativeX } : {}),
    ...(relativeY !== undefined ? { relativeY } : {}),
    ...(relativeStartX !== undefined ? { relativeStartX } : {}),
    ...(relativeStartY !== undefined ? { relativeStartY } : {}),
    ...(relativeWidth !== undefined ? { relativeWidth } : {}),
    ...(relativeHeight !== undefined ? { relativeHeight } : {}),
    savedCanvasWidth: width,
    savedCanvasHeight: height,
    ...(points ? { points } : {}),
  };
};

const getCanvasDimensions = (ctx: CommitContext): { width?: number; height?: number } => {
  const canvas = ctx.canvasRef?.current;
  if (!canvas) return {};
  if (!canUseCanvasDims(canvas.width, canvas.height)) return {};
  return { width: canvas.width, height: canvas.height };
};

const normalizeForPersistence = (rows: Annotation[], ctx: CommitContext): Annotation[] => {
  const { width, height } = getCanvasDimensions(ctx);
  const deduped = uniqueById(rows);
  const filtered = ctx.persistFilter ? deduped.filter(ctx.persistFilter) : deduped;
  return filtered.map((annotation) => normalizeRelativeGeometry(annotation, width, height));
};

const changedTypesFrom = (rows: Annotation[]) =>
  Array.from(new Set(rows.map((row) => row.component_type).filter(Boolean))) as string[];

const toNumberOrNull = (value: any): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? Number(value.toFixed(3)) : null;

export const getAnnotationsHash = (annotations: Annotation[]): string => {
  const sorted = [...annotations].sort((a, b) => a.id.localeCompare(b.id));
  return JSON.stringify(
    sorted.map((annotation) => ({
      id: annotation.id,
      page: annotation.page,
      componentType: annotation.component_type,
      annotationType: annotation.type,
      number: annotation.number ?? null,
      x: toNumberOrNull(annotation.x),
      y: toNumberOrNull(annotation.y),
      startX: toNumberOrNull(annotation.startX),
      startY: toNumberOrNull(annotation.startY),
      endX: toNumberOrNull(annotation.endX),
      endY: toNumberOrNull(annotation.endY),
      width: toNumberOrNull(annotation.width),
      height: toNumberOrNull(annotation.height),
      radius: toNumberOrNull(annotation.radius),
      relativeX: toNumberOrNull(annotation.relativeX),
      relativeY: toNumberOrNull(annotation.relativeY),
      relativeStartX: toNumberOrNull(annotation.relativeStartX),
      relativeStartY: toNumberOrNull(annotation.relativeStartY),
      relativeWidth: toNumberOrNull(annotation.relativeWidth),
      relativeHeight: toNumberOrNull(annotation.relativeHeight),
      pointsCount: Array.isArray((annotation as any).points) ? (annotation as any).points.length : 0,
    }))
  );
};

export const getAnnotationSignature = (annotation: Annotation): string =>
  JSON.stringify({
    id: annotation.id,
    page: annotation.page,
    componentType: annotation.component_type ?? null,
    annotationType: annotation.type ?? null,
    number: annotation.number ?? null,
    text: annotation.text ?? null,
    color: annotation.color ?? null,
    thickness: toNumberOrNull(annotation.thickness),
    x: toNumberOrNull(annotation.x),
    y: toNumberOrNull(annotation.y),
    startX: toNumberOrNull(annotation.startX),
    startY: toNumberOrNull(annotation.startY),
    endX: toNumberOrNull(annotation.endX),
    endY: toNumberOrNull(annotation.endY),
    width: toNumberOrNull(annotation.width),
    height: toNumberOrNull(annotation.height),
    radius: toNumberOrNull(annotation.radius),
    relativeX: toNumberOrNull(annotation.relativeX),
    relativeY: toNumberOrNull(annotation.relativeY),
    relativeStartX: toNumberOrNull(annotation.relativeStartX),
    relativeStartY: toNumberOrNull(annotation.relativeStartY),
    relativeWidth: toNumberOrNull(annotation.relativeWidth),
    relativeHeight: toNumberOrNull(annotation.relativeHeight),
    savedCanvasWidth: toNumberOrNull(annotation.savedCanvasWidth),
    savedCanvasHeight: toNumberOrNull(annotation.savedCanvasHeight),
    measurement: toNumberOrNull(annotation.measurement),
    measurementUnit: annotation.measurementUnit ?? null,
    totalLength: toNumberOrNull(annotation.totalLength),
    totalArea: toNumberOrNull(annotation.totalArea),
    areaUnit: annotation.areaUnit ?? null,
    wireTypeColor: annotation.wireTypeColor ?? null,
    wireTypeName: annotation.wireTypeName ?? null,
    auto_detected: annotation.auto_detected ?? null,
    confidence: toNumberOrNull(annotation.confidence),
    points: Array.isArray((annotation as any).points)
      ? (annotation as any).points.map((point: any) => ({
          x: toNumberOrNull(point?.x),
          y: toNumberOrNull(point?.y),
          relativeX: toNumberOrNull(point?.relativeX),
          relativeY: toNumberOrNull(point?.relativeY),
        }))
      : null,
  });

async function runLocked<T>(ctx: CommitContext, action: () => Promise<T>): Promise<T> {
  await ctx.acquireSaveLock();
  ctx.setIsSaving?.(true);
  try {
    return await action();
  } finally {
    ctx.releaseSaveLock();
    ctx.setIsSaving?.(false);
  }
}

export async function commitDeleteAnnotations(
  annotationIds: string[],
  ctx: CommitContext,
  knownComponentTypes?: string[]
): Promise<PersistResult> {
  const ids = Array.from(new Set((annotationIds || []).map((id) => String(id).trim()).filter(Boolean)));
  if (!ids.length) return { changedComponentTypes: [] };
  if (!ctx.supabase || !ctx.estimationId) {
    const changed = Array.from(new Set((knownComponentTypes || []).filter(Boolean)));
    await Promise.resolve(ctx.onAnnotationSaved?.(changed as string[]));
    return { changedComponentTypes: changed as string[] };
  }

  return runLocked(ctx, async () => {
    const { changedComponentTypes } = await deleteAnnotationsByIdV2({
      supabase: ctx.supabase,
      estimationId: ctx.estimationId,
      activeMode: ctx.activeMode,
      currentDrawingFileId: ctx.currentDrawingFileId ?? null,
      annotationIds: ids,
      knownComponentTypes,
    });
    await Promise.resolve(ctx.onAnnotationSaved?.(changedComponentTypes));
    return { changedComponentTypes };
  });
}

export async function commitUpdateAnnotations(
  patchById: Record<string, Partial<Annotation>>,
  ctx: CommitContext
): Promise<CommitUpdateResult> {
  const patchEntries = Object.entries(patchById || {})
    .map(([id, patch]) => [String(id || '').trim(), patch] as const)
    .filter(([id, patch]) => Boolean(id) && Boolean(patch));

  if (!patchEntries.length) {
    return { next: ctx.annotationsRef.current, changedComponentTypes: [], updatedIds: [] };
  }

  return runLocked(ctx, async () => {
    const prev = ctx.annotationsRef.current;
    const patchMap = new Map<string, Partial<Annotation>>(patchEntries);
    const mergedUpdates: Annotation[] = [];

    const next = prev.map((annotation) => {
      const patch = patchMap.get(annotation.id);
      if (!patch) return annotation;
      const merged = { ...annotation, ...patch, id: annotation.id } as Annotation;
      mergedUpdates.push(merged);
      return merged;
    });

    if (!mergedUpdates.length) {
      return { next: prev, changedComponentTypes: [], updatedIds: [] };
    }

    const normalizedUpdates = normalizeForPersistence(mergedUpdates, ctx);
    if (!normalizedUpdates.length) {
      return { next: prev, changedComponentTypes: [], updatedIds: [] };
    }

    const normalizedById = new Map<string, Annotation>(
      normalizedUpdates.map((annotation) => [annotation.id, annotation])
    );
    const nextWithNormalized = next.map(
      (annotation) => normalizedById.get(annotation.id) ?? annotation
    );
    const updatedIds = normalizedUpdates.map((annotation) => annotation.id);

    if (!ctx.supabase || !ctx.estimationId) {
      const changed = changedTypesFrom(normalizedUpdates);
      await Promise.resolve(ctx.onAnnotationSaved?.(changed));
      return { next: nextWithNormalized, changedComponentTypes: changed, updatedIds };
    }

    const { changedComponentTypes } = await saveAnnotationSnapshotV2({
      supabase: ctx.supabase,
      estimationId: ctx.estimationId,
      activeMode: ctx.activeMode,
      currentDrawingFileId: ctx.currentDrawingFileId ?? null,
      annotations: normalizedUpdates,
    });

    await Promise.resolve(ctx.onAnnotationSaved?.(changedComponentTypes));
    return { next: nextWithNormalized, changedComponentTypes, updatedIds };
  });
}

export async function commitClearScopeAnnotations(
  ctx: CommitContext
): Promise<CommitClearScopeResult> {
  if (!ctx.supabase || !ctx.estimationId) {
    return { changedComponentTypes: [], deletedCount: 0 };
  }

  return runLocked(ctx, async () => {
    let deleteQuery = ctx.supabase
      .from('annotation_entities_v2')
      .delete()
      .eq('estimation_id', ctx.estimationId);

    if (ctx.activeMode === 'draw') {
      if (!ctx.currentDrawingFileId) {
        console.warn('⏭️ Aborting clear scope save: draw mode without file scope');
        return { changedComponentTypes: [], deletedCount: 0 };
      }
      deleteQuery = deleteQuery
        .eq('scope_type', 'file')
        .eq('file_id', ctx.currentDrawingFileId);
    } else {
      deleteQuery = deleteQuery
        .eq('scope_type', 'global')
        .is('file_id', null);
    }

    const { data: deletedRows, error } = await deleteQuery.select('component_type');
    if (error) throw error;

    const changedComponentTypes = Array.from(
      new Set((deletedRows || []).map((row: any) => row?.component_type).filter(Boolean))
    ) as string[];
    await Promise.resolve(ctx.onAnnotationSaved?.(changedComponentTypes));

    return {
      changedComponentTypes,
      deletedCount: deletedRows?.length || 0,
    };
  });
}

export async function commitCreateAnnotations(
  incoming: Annotation[],
  ctx: CommitContext
): Promise<CommitCreateResult> {
  const normalizedIncoming = normalizeForPersistence(incoming, ctx);
  if (!normalizedIncoming.length) {
    return { next: ctx.annotationsRef.current, changedComponentTypes: [] };
  }

  return runLocked(ctx, async () => {
    const prev = ctx.annotationsRef.current;
    const prevIds = new Set(prev.map((annotation) => annotation.id));
    const toAppend = normalizedIncoming.filter((annotation) => !prevIds.has(annotation.id));
    const next = toAppend.length ? [...prev, ...toAppend] : prev;

    if (toAppend.length) {
      ctx.beforeLocalCommit?.();
      ctx.annotationsRef.current = next;
      ctx.setAnnotations(next);
    }

    if (!ctx.supabase || !ctx.estimationId) {
      const changed = changedTypesFrom(normalizedIncoming);
      await Promise.resolve(ctx.onAnnotationSaved?.(changed));
      return { next, changedComponentTypes: changed };
    }

    try {
      const { changedComponentTypes } = await saveAnnotationSnapshotV2({
        supabase: ctx.supabase,
        estimationId: ctx.estimationId,
        activeMode: ctx.activeMode,
        currentDrawingFileId: ctx.currentDrawingFileId ?? null,
        annotations: normalizedIncoming,
      });

      await Promise.resolve(ctx.onAnnotationSaved?.(changedComponentTypes));
      return { next, changedComponentTypes };
    } catch (error) {
      ctx.annotationsRef.current = prev;
      ctx.setAnnotations(prev);
      throw error;
    }
  });
}